/* =========================================================
   Shoppable Video — behavior
   Vanilla JS, no dependencies. Safe to include once per page
   even if multiple "Shoppable video" sections are present.
   ========================================================= */

(function () {
  'use strict';

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  /* ---------------------------------------------------------
     Carousel controller (grid sections skip this entirely)
     --------------------------------------------------------- */
  function initCarousel(section) {
    if (section.dataset.type !== 'carousel') return;

    var track = qs('[data-svs-track]', section);
    if (!track) return;

    var prevBtn = qs('[data-svs-prev]', section);
    var nextBtn = qs('[data-svs-next]', section);
    var dotsWrap = qs('[data-svs-dots]', section);
    var infinite = section.dataset.infinite === 'true';
    var autoplay = section.dataset.autoplay === 'true';
    var columns = parseInt(getComputedStyle(track).getPropertyValue('--svs-columns')) || 4;
    var autoplayTimer = null;
    var isPointerDown = false;

    function items() { return qsa('[data-svs-item]', track); }

    function itemStep() {
      var first = items()[0];
      if (!first) return 0;
      var gap = parseFloat(getComputedStyle(track).gap) || 0;
      return first.getBoundingClientRect().width + gap;
    }

    function atStart() { return track.scrollLeft <= 2; }
    function atEnd() { return track.scrollLeft + track.clientWidth >= track.scrollWidth - 2; }

    function goNext() {
      if (atEnd()) {
        if (infinite) track.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }
      track.scrollBy({ left: itemStep(), behavior: 'smooth' });
    }

    function goPrev() {
      if (atStart()) {
        if (infinite) track.scrollTo({ left: track.scrollWidth, behavior: 'smooth' });
        return;
      }
      track.scrollBy({ left: -itemStep(), behavior: 'smooth' });
    }

    if (nextBtn) nextBtn.addEventListener('click', function () { stopAutoplay(); goNext(); startAutoplay(); });
    if (prevBtn) prevBtn.addEventListener('click', function () { stopAutoplay(); goPrev(); startAutoplay(); });

    /* Dots */
    function buildDots() {
      if (!dotsWrap) return;
      var total = items().length;
      var pageCount = Math.max(1, Math.ceil(total / Math.max(1, columns)));
      dotsWrap.innerHTML = '';
      for (var i = 0; i < pageCount; i++) {
        var b = document.createElement('button');
        b.type = 'button';
        b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        b.setAttribute('aria-current', i === 0 ? 'true' : 'false');
        (function (pageIndex) {
          b.addEventListener('click', function () {
            stopAutoplay();
            track.scrollTo({ left: pageIndex * columns * itemStep(), behavior: 'smooth' });
            startAutoplay();
          });
        })(i);
        dotsWrap.appendChild(b);
      }
    }

    function updateDots() {
      if (!dotsWrap) return;
      var step = itemStep() || 1;
      var currentItem = Math.round(track.scrollLeft / step);
      var currentPage = Math.floor(currentItem / Math.max(1, columns));
      qsa('button', dotsWrap).forEach(function (b, i) {
        b.setAttribute('aria-current', i === currentPage ? 'true' : 'false');
      });
    }

    var scrollRaf = null;
    track.addEventListener('scroll', function () {
      if (scrollRaf) return;
      scrollRaf = requestAnimationFrame(function () { updateDots(); scrollRaf = null; });
    });

    function startAutoplay() {
      if (!autoplay || autoplayTimer) return;
      autoplayTimer = setInterval(function () {
        if (isPointerDown) return;
        goNext();
      }, 4000);
    }

    function stopAutoplay() {
      if (autoplayTimer) { clearInterval(autoplayTimer); autoplayTimer = null; }
    }

    section.addEventListener('mouseenter', stopAutoplay);
    section.addEventListener('mouseleave', startAutoplay);
    track.addEventListener('pointerdown', function () { isPointerDown = true; });
    track.addEventListener('pointerup', function () { isPointerDown = false; });

    buildDots();
    updateDots();
    startAutoplay();
  }

  /* ---------------------------------------------------------
     Modal controller — one per section, reused for each video
     --------------------------------------------------------- */
  function initModal(section) {
    var sectionId = section.dataset.sectionId;
    var modal = document.getElementById('ShoppableVideoModal-' + sectionId);
    if (!modal) return;

    var videoEl = qs('[data-svs-video]', modal);
    var noVideoEl = qs('[data-svs-no-video]', modal);
    var productTrack = qs('[data-svs-product-track]', modal);
    var productDots = qs('[data-svs-product-dots]', modal);
    var productPrev = qs('[data-svs-product-prev]', modal);
    var productNext = qs('[data-svs-product-next]', modal);

    var currentProducts = [];
    var currentProductIndex = 0;
    var lastFocusedTrigger = null;

    function openModal(data, triggerEl) {
      lastFocusedTrigger = triggerEl;
      currentProducts = data.products || [];
      currentProductIndex = 0;

      if (data.video) {
        videoEl.hidden = false;
        noVideoEl.hidden = true;
        videoEl.poster = data.poster || '';
        videoEl.src = data.video;
      } else {
        videoEl.hidden = true;
        noVideoEl.hidden = false;
      }

      renderProducts();
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';

      /* Pause all muted card videos while the modal is open */
      if (section._svsCardPause) section._svsCardPause();

      if (data.video) {
        videoEl.play().catch(function () { /* autoplay might be blocked, ignore */ });
      }

      modal.querySelector('.shoppable-video-modal__close').focus();
    }

    function closeModal() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      videoEl.pause();
      videoEl.removeAttribute('src');
      videoEl.load();
      /* Resume muted card videos once modal is dismissed */
      if (section._svsCardResume) section._svsCardResume();
      if (lastFocusedTrigger) lastFocusedTrigger.focus();
    }

    qsa('[data-shoppable-close]', modal).forEach(function (el) {
      el.addEventListener('click', closeModal);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) closeModal();
    });

    /* ---- Product slider (between multiple tagged products) ---- */
    function renderProducts() {
      productTrack.innerHTML = '';
      var multi = currentProducts.length > 1;
      productPrev.hidden = !multi;
      productNext.hidden = !multi;

      currentProducts.forEach(function (product, index) {
        var slide = buildProductSlide(product);
        slide.classList.toggle('is-active', index === 0);
        productTrack.appendChild(slide);
      });

      buildProductDots();
    }

    function buildProductDots() {
      productDots.innerHTML = '';
      if (currentProducts.length <= 1) return;
      currentProducts.forEach(function (_, index) {
        var b = document.createElement('button');
        b.type = 'button';
        b.setAttribute('aria-label', 'Show product ' + (index + 1));
        b.setAttribute('aria-current', index === 0 ? 'true' : 'false');
        b.addEventListener('click', function () { showProduct(index); });
        productDots.appendChild(b);
      });
    }

    function showProduct(index) {
      if (!currentProducts.length) return;
      currentProductIndex = (index + currentProducts.length) % currentProducts.length;
      qsa('.svs-product-slide', productTrack).forEach(function (el, i) {
        el.classList.toggle('is-active', i === currentProductIndex);
      });
      qsa('button', productDots).forEach(function (b, i) {
        b.setAttribute('aria-current', i === currentProductIndex ? 'true' : 'false');
      });
    }

    productPrev.addEventListener('click', function () { showProduct(currentProductIndex - 1); });
    productNext.addEventListener('click', function () { showProduct(currentProductIndex + 1); });

    /* ---- Build a single product slide: gallery + info + variants + actions ---- */
    function buildProductSlide(product) {
      var slide = document.createElement('div');
      slide.className = 'svs-product-slide';

      /* Image gallery */
      var gallery = document.createElement('div');
      gallery.className = 'svs-gallery';
      var galleryTrack = document.createElement('div');
      galleryTrack.className = 'svs-gallery__track';
      var images = (product.images && product.images.length) ? product.images : [product.image].filter(Boolean);

      /* Show a low-res placeholder instantly using the thumb from JSON */
      var skeleton = document.createElement('div');
      skeleton.className = 'svs-gallery__skeleton';
      if (product.thumb) {
        skeleton.style.backgroundImage = 'url(' + product.thumb + ')';
        skeleton.style.backgroundSize = 'cover';
        skeleton.style.backgroundPosition = 'center';
        skeleton.style.filter = 'blur(6px)';
      }
      gallery.appendChild(skeleton);

      images.forEach(function (src, idx) {
        var slideImg = document.createElement('div');
        slideImg.className = 'svs-gallery__slide';
        var img = document.createElement('img');
        /* First image: load eagerly + high priority so it shows instantly */
        if (idx === 0) {
          img.src = src;
          img.loading = 'eager';
          img.setAttribute('fetchpriority', 'high');
          img.decoding = 'async';
          /* Hide skeleton once first image is decoded */
          img.addEventListener('load', function () {
            skeleton.style.display = 'none';
          });
        } else {
          /* Rest: only start loading once user scrolls to them */
          img.loading = 'lazy';
          img.dataset.src = src;
          img.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
          /* Lazy-swap src when slide scrolls into view */
          if ('IntersectionObserver' in window) {
            var lazyObserver = new IntersectionObserver(function(entries, obs) {
              entries.forEach(function(entry) {
                if (entry.isIntersecting) {
                  var el = entry.target;
                  if (el.dataset.src) { el.src = el.dataset.src; delete el.dataset.src; }
                  obs.unobserve(el);
                }
              });
            }, { rootMargin: '200px' });
            lazyObserver.observe(img);
          } else {
            img.src = src;
          }
        }
        img.alt = product.title;
        img.width = 600;
        img.height = 600;
        slideImg.appendChild(img);
        galleryTrack.appendChild(slideImg);
      });
      gallery.appendChild(galleryTrack);

      if (images.length > 1) {
        var gPrev = document.createElement('button');
        gPrev.type = 'button';
        gPrev.className = 'svs-gallery__nav svs-gallery__nav--prev';
        gPrev.setAttribute('aria-label', 'Previous image');
        gPrev.innerHTML = '&#8249;';
        gPrev.addEventListener('click', function () {
          galleryTrack.scrollBy({ left: -galleryTrack.clientWidth, behavior: 'smooth' });
        });

        var gNext = document.createElement('button');
        gNext.type = 'button';
        gNext.className = 'svs-gallery__nav svs-gallery__nav--next';
        gNext.setAttribute('aria-label', 'Next image');
        gNext.innerHTML = '&#8250;';
        gNext.addEventListener('click', function () {
          galleryTrack.scrollBy({ left: galleryTrack.clientWidth, behavior: 'smooth' });
        });

        gallery.appendChild(gPrev);
        gallery.appendChild(gNext);
      }

      slide.appendChild(gallery);

      /* Title — links to the product page */
      var title = document.createElement('h3');
      title.className = 'svs-product-title';
      var titleLink = document.createElement('a');
      titleLink.className = 'svs-product-title__link';
      titleLink.href = product.url || '#';
      titleLink.textContent = product.title;
      title.appendChild(titleLink);
      slide.appendChild(title);

      /* Price */
      var priceWrap = document.createElement('div');
      priceWrap.className = 'svs-product-price';
      var priceNow = document.createElement('span');
      priceNow.className = 'svs-product-price__now';
      priceNow.textContent = product.price;
      priceWrap.appendChild(priceNow);
      if (product.compare_at_price_raw && product.compare_at_price_raw > product.price_raw) {
        var priceCompare = document.createElement('span');
        priceCompare.className = 'svs-product-price__compare';
        priceCompare.textContent = product.compare_at_price;
        priceWrap.appendChild(priceCompare);
      }
      slide.appendChild(priceWrap);

      /* Variant options — skip entirely when there's nothing to choose
         (e.g. a product with no real variants only has a single
         "Default Title" combination, so there's no picker to show) */
      var selectedOptions = [];
      var optionRows = [];
      var hasRealVariants = product.variants && product.variants.length > 1;

      if (hasRealVariants && product.options && product.options.length) {
        product.options.forEach(function (option, optionIndex) {
          selectedOptions[optionIndex] = option.values[0];

          var row = document.createElement('div');
          row.className = 'svs-option';
          var label = document.createElement('span');
          label.className = 'svs-option__label';
          label.textContent = option.name;
          row.appendChild(label);

          var valuesWrap = document.createElement('div');
          valuesWrap.className = 'svs-option__values';

          option.values.forEach(function (value) {
            var btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'svs-option__value';
            btn.textContent = value;
            if (value === selectedOptions[optionIndex]) btn.classList.add('is-selected');
            btn.addEventListener('click', function () {
              selectedOptions[optionIndex] = value;
              qsa('.svs-option__value', valuesWrap).forEach(function (b) {
                b.classList.toggle('is-selected', b.textContent === value);
              });
              updateVariantState();
            });
            valuesWrap.appendChild(btn);
          });

          row.appendChild(valuesWrap);
          slide.appendChild(row);
          optionRows.push({ row: row, valuesWrap: valuesWrap });
        });
      }

      /* Actions */
      var actions = document.createElement('div');
      actions.className = 'svs-actions';

      var addBtn = document.createElement('button');
      addBtn.type = 'button';
      addBtn.className = 'svs-add-to-cart';
      addBtn.textContent = 'Add to cart';
      actions.appendChild(addBtn);

      var wishlistBtn = document.createElement('button');
      wishlistBtn.type = 'button';
      wishlistBtn.className = 'svs-wishlist';
      wishlistBtn.setAttribute('aria-label', 'Add to wishlist');
      wishlistBtn.innerHTML = '&#9825;';
      wishlistBtn.addEventListener('click', function () {
        wishlistBtn.classList.toggle('is-active');
        wishlistBtn.innerHTML = wishlistBtn.classList.contains('is-active') ? '&#9829;' : '&#9825;';
        document.dispatchEvent(new CustomEvent('shoppable-video:wishlist-toggle', {
          detail: { productId: product.id, added: wishlistBtn.classList.contains('is-active') }
        }));
      });
      actions.appendChild(wishlistBtn);

      slide.appendChild(actions);

      var feedback = document.createElement('div');
      feedback.className = 'svs-product-feedback';
      slide.appendChild(feedback);

      /* Resolve current variant + update UI state */
      function findVariant() {
        if (!product.variants) return null;
        return product.variants.find(function (v) {
          return (selectedOptions[0] === undefined || v.option1 === selectedOptions[0]) &&
                 (selectedOptions[1] === undefined || v.option2 === selectedOptions[1]) &&
                 (selectedOptions[2] === undefined || v.option3 === selectedOptions[2]);
        }) || null;
      }

      function updateVariantState() {
        var variant = findVariant();

        if (variant) {
          priceNow.textContent = variant.price;
          if (variant.featured_image) {
            var firstSlide = galleryTrack.querySelector('.svs-gallery__slide');
            if (firstSlide) firstSlide.querySelector('img').src = variant.featured_image;
          }
        }

        var available = variant ? variant.available : product.available;
        addBtn.disabled = !available;
        addBtn.textContent = available ? 'Add to cart' : 'Sold out';
        addBtn.classList.remove('is-added');
        addBtn.dataset.variantId = variant ? variant.id : '';
      }

      addBtn.addEventListener('click', function () {
        var variantId = addBtn.dataset.variantId;
        if (!variantId) return;

        addBtn.disabled = true;
        feedback.textContent = 'Adding…';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        })
          .then(function (res) {
            if (!res.ok) return res.json().then(function (err) { throw err; });
            return res.json();
          })
          .then(function (item) {
            addBtn.classList.add('is-added');
            addBtn.textContent = 'Added ✓';
            feedback.textContent = 'Added to cart.';
            addBtn.disabled = false;
            document.dispatchEvent(new CustomEvent('shoppable-video:added-to-cart', { detail: item }));
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            setTimeout(function () {
              addBtn.classList.remove('is-added');
              updateVariantState();
            }, 2200);
          })
          .catch(function (err) {
            addBtn.disabled = false;
            feedback.textContent = (err && err.description) ? err.description : 'Could not add to cart.';
          });
      });

      updateVariantState();

      return slide;
    }

    /* ---- Trigger buttons inside this section open this modal ---- */
    qsa('[data-shoppable-trigger]', section).forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var targetId = trigger.getAttribute('data-target');
        var dataEl = document.getElementById(targetId);
        if (!dataEl) return;
        try {
          var data = JSON.parse(dataEl.textContent);
          openModal(data, trigger);
        } catch (e) {
          /* malformed JSON in the data block — fail silently in production */
        }
      });
    });
  }

  /* ---------------------------------------------------------
     Card video autoplay — plays all card videos muted when
     the section is in the viewport, pauses when it leaves.
     Uses IntersectionObserver for performance (no scroll events).
     --------------------------------------------------------- */
  function initCardAutoplay(section) {
    var cardVideos = qsa('[data-svs-card-video]', section);
    if (!cardVideos.length) return;

    /* Load the src only once (lazy-load via data-src) */
    function loadVideo(video) {
      if (video.dataset.loaded) return;
      video.src = video.dataset.src;
      video.dataset.loaded = '1';
    }

    function playVideo(video) {
      loadVideo(video);
      /* Hide the poster img once the video is playing to avoid flash */
      video.addEventListener('canplay', function onCanPlay() {
        var poster = video.nextElementSibling;
        if (poster && poster.classList.contains('shoppable-video__card-poster')) {
          poster.style.opacity = '0';
        }
        video.removeEventListener('canplay', onCanPlay);
      });
      var p = video.play();
      if (p && p.catch) p.catch(function () { /* autoplay blocked — poster stays visible */ });
    }

    function pauseVideo(video) {
      if (!video.paused) video.pause();
    }

    /* One observer per section watches the section wrapper.
       When >=15% of the section is visible, play all card videos.
       When it leaves view, pause them to save bandwidth/battery. */
    if ('IntersectionObserver' in window) {
      var sectionObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          cardVideos.forEach(function (v) {
            if (entry.isIntersecting) {
              playVideo(v);
            } else {
              pauseVideo(v);
            }
          });
        });
      }, { threshold: 0.15 });

      sectionObserver.observe(section);
    } else {
      /* Fallback for older browsers — just load + play immediately */
      cardVideos.forEach(function (v) { playVideo(v); });
    }

    /* Expose helpers so the modal can pause/resume card videos */
    section._svsCardPause = function () { cardVideos.forEach(pauseVideo); };
    section._svsCardResume = function () {
      cardVideos.forEach(function (v) {
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
      });
    };
  }

  function init() {
    qsa('.shoppable-video-section').forEach(function (section) {
      initCarousel(section);
      initCardAutoplay(section);
      initModal(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
